import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { I as Input } from '../Input/Input-jeHhNn_Y.js';
import { P as PillBox } from './PillBox-DAbSMb3d.js';
import { g as getTranslationComponent } from '../i18n/ListBuilderInputTranslation/static-SL0AuvKM.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { I as IntlProvider } from '../LocalModules/storm-ui-intl/dist/esm/IntlProvider-DmRs8GLp-yq9BpgTU.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-BLE3wELi-CV9fXVbx.js';
import { B as Button } from '../Button/Button-_vtWxpaL.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';

const ListBuilderWrapper = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40019350__sc-qxh3rc-0"
})(["height:100%;width:100%;display:flex;flex-direction:column;background-color:", ";"], ({
  theme
}) => theme.surface.default);
const ListBuilderBaseWrapper = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40019350__sc-qxh3rc-1"
})(["display:flex;flex:0 0 auto;flex-direction:column;border:", ";border-radius:", ";padding-block-start:", ";padding-block-end:", ";padding-inline-start:", ";padding-inline-end:", ";", ""], ({
  theme
}) => theme.border.medium.default, ({
  theme,
  $hasItems
}) => $hasItems ? `${theme.border.radius.small} ${theme.border.radius.small} 0 0` : theme.border.radius.small, ({
  theme
}) => theme.spacing.xsmall.default, ({
  theme,
  $hasMessage
}) => $hasMessage ? theme.spacing.xxxsmall.default : theme.spacing.xsmall.default, ({
  theme
}) => theme.spacing.xsmall.default, ({
  theme
}) => theme.spacing.xsmall.default, isMobile(css(["padding-block-start:", ";padding-block-end:", ";padding-inline-start:", ";padding-inline-end:", ";"], ({
  theme
}) => theme.spacing.xsmall.mobile, ({
  theme,
  $hasMessage
}) => $hasMessage ? theme.spacing.xxxsmall.mobile : theme.spacing.xsmall.mobile, ({
  theme
}) => theme.spacing.xsmall.mobile, ({
  theme
}) => theme.spacing.xsmall.mobile)));
const AddButton = /*#__PURE__*/styled(Button).withConfig({
  componentId: "sc-storm-ui-40019350__sc-qxh3rc-2"
})(["align-items:center;", ""], isMobile(css(["height:", ";"], ({
  theme
}) => `calc(${theme.controls.paddingInline.large.mobile} + ${theme.controls.paddingBlock.base.mobile} + ${theme.spacing.base.mobile} + 2px)`)));
const ListBuilderInputWrapper = /*#__PURE__*/styled('form').withConfig({
  componentId: "sc-storm-ui-40019350__sc-qxh3rc-3"
})(["display:flex;flex:1 1 auto;align-items:flex-start;justify-content:space-between;gap:", ";", ""], ({
  theme
}) => theme.spacing.xsmall.default, isMobile(css(["gap:", ";"], ({
  theme
}) => theme.spacing.xsmall.mobile)));
const ListBuilderComponent = ({
  id,
  addText,
  items = [],
  onAdd,
  onRemovePill,
  onRemoveAll,
  removeAllLabel,
  disableAdd,
  onChange,
  disabled,
  message,
  ...rest
}) => {
  const [inputVal, setInputVal] = useState('');
  const pillBoxRef = useRef(null);
  const inputRef = useRef(null);
  const handleInputChange = event => {
    var _event$target;
    setInputVal((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
    onChange === null || onChange === void 0 || onChange(event);
  };
  const handleOnAdd = event => {
    onAdd === null || onAdd === void 0 || onAdd(event, inputVal);
    setInputVal('');
  };
  const handleOnRemoveAll = event => {
    var _inputRef$current;
    // focus the wrapper when the remove all button is clicked
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    onRemoveAll === null || onRemoveAll === void 0 || onRemoveAll(event);
  };
  const handleOnRemovePill = (event, item) => {
    const pillElements = getFocusableElements(pillBoxRef.current);
    const currentIndex = pillElements.findIndex(ele => ele === event.currentTarget);
    onRemovePill === null || onRemovePill === void 0 || onRemovePill(event, item);

    // Wait for next tick to allow DOM to update
    setTimeout(() => {
      var _remainingPills$targe;
      // If there are no pills in the box, focus on the input
      const remainingPills = getFocusableElements(pillBoxRef.current);
      if (!(remainingPills !== null && remainingPills !== void 0 && remainingPills.length)) {
        var _inputRef$current2;
        (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
        return;
      }

      // Try to focus previous pill, if not available focus next pill
      const targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      (_remainingPills$targe = remainingPills[targetIndex]) === null || _remainingPills$targe === void 0 || _remainingPills$targe.focus();
    }, 0);
  };
  const {
    formatMessage
  } = useIntl();
  return /*#__PURE__*/React__default.createElement(ListBuilderWrapper, {
    id: id
  }, /*#__PURE__*/React__default.createElement(ListBuilderBaseWrapper, {
    $hasMessage: !!message,
    $hasItems: (items === null || items === void 0 ? void 0 : items.length) > 0
  }, /*#__PURE__*/React__default.createElement(ListBuilderInputWrapper, {
    onSubmit: event => event.preventDefault()
  }, /*#__PURE__*/React__default.createElement(Input, _extends({}, rest, {
    inputRef: inputRef,
    fullWidth: true,
    id: `${id}-input`,
    value: inputVal,
    onChange: handleInputChange,
    disabled: disabled,
    message: message
  })), /*#__PURE__*/React__default.createElement(AddButton, {
    type: "submit",
    onClick: handleOnAdd,
    disabled: disabled || disableAdd,
    tabIndex: 0
  }, addText !== null && addText !== void 0 ? addText : formatMessage({
    id: 'button_add'
  })))), items && items.length ? /*#__PURE__*/React__default.createElement(PillBox, {
    pills: items,
    onRemovePill: handleOnRemovePill,
    onRemoveAll: handleOnRemoveAll,
    removeAllLabel: removeAllLabel !== null && removeAllLabel !== void 0 ? removeAllLabel : formatMessage({
      id: 'button_remove_all'
    }),
    ref: pillBoxRef
  }) : null);
};
const ListBuilderInput = ({
  locale = 'en-US',
  ...rest
}) => {
  const translations = getTranslationComponent(locale);
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    translations: translations
  }, /*#__PURE__*/React__default.createElement(ListBuilderComponent, rest));
};

export { ListBuilderInput as L };
//# sourceMappingURL=ListBuilderInput-Dg1AnVZi.js.map
